import { useState, useMemo, useEffect } from 'react';
import { SideListCard, FilterChip, SearchBar, Pagination, type FilterGroup } from '@practium/ui';
import { type ParcelamentoAPI } from '../../../types';
import { formatParcellingDescription } from '../../../utils';
import { CULTURE_LABELS, CROP_STAGE_LABELS } from '../../../constants';
import styles from './ParcellingListCard.module.css';

const PAGE_SIZE = 15;

interface ParcellingListCardProps {
	parcellings: ParcelamentoAPI[];
	selectedId: string | null;
	onSelect: (id: string) => void;
	page?: number;
	totalPages?: number;
	onPageChange?: (page: number) => void;
}

export const ParcellingListCard = ({
	parcellings,
	selectedId,
	onSelect,
	page: controlledPage,
	totalPages: controlledTotalPages,
	onPageChange,
}: ParcellingListCardProps) => {
	const [filterValues, setFilterValues] = useState<string[]>([]);
	const [search, setSearch] = useState('');
	const [internalPage, setInternalPage] = useState(1);
	const page = controlledPage ?? internalPage;
	const handlePageChange = (nextPage: number) => {
		if (onPageChange) {
			onPageChange(nextPage);
			return;
		}
		setInternalPage(nextPage);
	};

	const filterGroups: FilterGroup[] = useMemo(() => {
		const cultures = [...new Set(parcellings.map((p) => p.culture))];
		const stages = [...new Set(parcellings.map((p) => p.cropStage))];

		return [
			{
				label: 'Cultura',
				options: cultures.map((c) => ({ value: `culture:${c}`, label: CULTURE_LABELS[c] ?? c })),
			},
			{
				label: 'Estágio',
				options: stages.map((s) => ({ value: `stage:${s}`, label: CROP_STAGE_LABELS[s] ?? s })),
			},
			{
				label: 'Visibilidade',
				options: [{ value: 'visibility:global', label: 'Global' }],
			},
		];
	}, [parcellings]);

	const filtered = useMemo(() => {
		const q = search.trim().toLowerCase();
		const cultures = filterValues
			.filter((v) => v.startsWith('culture:'))
			.map((v) => v.replace('culture:', ''));
		const stages = filterValues
			.filter((v) => v.startsWith('stage:'))
			.map((v) => v.replace('stage:', ''));
		const onlyGlobal = filterValues.includes('visibility:global');

		return parcellings.filter((p) => {
			const nameOk = !q || (p.name || '').toLowerCase().includes(q);
			const cultureOk = cultures.length === 0 || cultures.includes(p.culture);
			const stageOk = stages.length === 0 || stages.includes(p.cropStage);
			const visibilityOk = !onlyGlobal || p.isPublic;
			return nameOk && cultureOk && stageOk && visibilityOk;
		});
	}, [parcellings, filterValues, search]);

	useEffect(() => {
		if (onPageChange) {
			onPageChange(1);
			return;
		}
		setInternalPage(1);
	}, [filterValues, search, onPageChange]);

	const totalPages = Math.max(
		1,
		controlledTotalPages ?? Math.ceil(filtered.length / PAGE_SIZE)
	);

	const items = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map((p) => ({
		id: p._id,
		label: p.name || 'Novo Parcelamento',
		sublabel: formatParcellingDescription(p),
		badge: p.isPublic ? 'Global' : undefined,
	}));

	return (
		<SideListCard
			title="Todos os Parcelamentos"
			subtitle="Selecione o parcelamento que deseja visualizar"
			items={items}
			activeId={selectedId ?? undefined}
			onSelect={onSelect}
			stretch={true}
			filterSlot={
				<div className={styles.toolbar}>
					<FilterChip
						values={filterValues}
						onChange={setFilterValues}
						groups={filterGroups}
						placeholder="Filtros"
					/>
					<div className={styles.searchWrapper}>
						<SearchBar value={search} onChange={setSearch} placeholder="Buscar por nome..." />
					</div>
				</div>
			}
			footerSlot={<Pagination page={page} totalPages={totalPages} onPageChange={handlePageChange} />}
		/>
	);
};
